import type { AnswerResult, MapSpec, ServedQuestion } from '@cruz/shared';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, errorMessage } from './api.ts';
import { clockOffset, formatSeconds, remainingMs } from './clock.ts';
import { loadPending, newIdempotencyKey, savePending, type PendingAnswer } from './device.ts';
import { Notice, primaryButton, Prompt } from './ui.tsx';

// Leaflet only loads when a map question appears: the chat never pays for it
const RouteMap = lazy(() => import('./RouteMap.tsx'));

/** While playing, a dropped connection is retried for a few minutes before giving up. */
const PLAY_RETRIES = 30;
/** Feedback stays this long before the next question comes on its own. */
const FEEDBACK_MS = 1800;

type State =
  | { kind: 'loading' }
  | { kind: 'question'; q: ServedQuestion; offset: number; sending: number | null | undefined }
  | { kind: 'answered'; q: ServedQuestion; offset: number; result: AnswerResult }
  | { kind: 'error'; message: string };

export function PlayScreen({
  code,
  token,
  onFinished,
}: {
  code: string;
  token: string;
  onFinished: () => void;
}) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [reconnecting, setReconnecting] = useState(false);
  const busy = useRef(false);
  const opts = { retries: PLAY_RETRIES, onRetry: () => setReconnecting(true) };

  /** Sends an answer, keeping it on the device until the server has it. */
  const send = useCallback(
    async (pending: PendingAnswer) => {
      savePending(code, pending);
      const result = await api.answer(code, token, pending, opts);
      savePending(code, null);
      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [code, token],
  );

  /** The next screen: a question, an error, or null when the game is over. */
  const fetchNext = useCallback(async (): Promise<State | null> => {
    try {
      // An answer sent before a reload or a lost connection goes first, with its key
      const pending = loadPending(code);
      if (pending) await send(pending);
      const sentAt = Date.now();
      const r = await api.next(code, token, opts);
      if (r.status === 'finished') return null;
      return {
        kind: 'question',
        q: r,
        offset: clockOffset(r.server_now, sentAt, Date.now()),
        sending: undefined,
      };
    } catch (err) {
      if (err instanceof ApiError && err.body?.error === 'closed') return null;
      return { kind: 'error', message: errorMessage(err) };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, token, send]);

  const loadNext = useCallback(() => {
    if (busy.current) return;
    busy.current = true;
    setState({ kind: 'loading' });
    void fetchNext().then((next) => {
      busy.current = false;
      setReconnecting(false);
      if (next) setState(next);
      else onFinished();
    });
  }, [fetchNext, onFinished]);

  // First question (the state already starts as loading)
  useEffect(() => {
    if (busy.current) return;
    busy.current = true;
    void fetchNext().then((next) => {
      busy.current = false;
      setReconnecting(false);
      if (next) setState(next);
      else onFinished();
    });
  }, [fetchNext, onFinished]);

  const choose = useCallback(
    async (choice: number | null) => {
      if (state.kind !== 'question' || state.sending !== undefined) return;
      const { q, offset } = state;
      setState({ ...state, sending: choice });
      try {
        const result = await send({
          position: q.position,
          choice,
          idempotency_key: newIdempotencyKey(),
        });
        setReconnecting(false);
        setState({ kind: 'answered', q, offset, result });
      } catch (err) {
        setState({ kind: 'error', message: errorMessage(err) });
      }
    },
    [state, send],
  );

  // Feedback for a moment, then the next question (the game clock keeps running)
  useEffect(() => {
    if (state.kind !== 'answered') return;
    const t = setTimeout(loadNext, FEEDBACK_MS);
    return () => clearTimeout(t);
  }, [state, loadNext]);

  return (
    <div className="space-y-4">
      {reconnecting && <Notice tone="warn">Reconectando… no cierres la página.</Notice>}
      {state.kind === 'loading' && (
        <p className="py-10 text-center text-lg text-ink-soft dark:text-cream/75">
          Cargando la pregunta…
        </p>
      )}
      {state.kind === 'error' && (
        <div className="space-y-3">
          <Notice tone="error">{state.message}</Notice>
          <button type="button" className={primaryButton} onClick={loadNext}>
            Reintentar
          </button>
        </div>
      )}
      {(state.kind === 'question' || state.kind === 'answered') && (
        <QuestionCard
          q={state.q}
          offset={state.offset}
          sending={state.kind === 'question' ? state.sending : undefined}
          result={state.kind === 'answered' ? state.result : null}
          onChoose={(c) => void choose(c)}
          onNext={loadNext}
        />
      )}
    </div>
  );
}

function Countdown({
  deadline,
  offset,
  seconds,
  stopped,
  onTimeUp,
}: {
  deadline: string;
  offset: number;
  seconds: number;
  stopped: boolean;
  onTimeUp: () => void;
}) {
  const [left, setLeft] = useState(() => remainingMs(deadline, offset));
  const fired = useRef(false);
  useEffect(() => {
    if (stopped) return;
    const tick = () => {
      const ms = remainingMs(deadline, offset);
      setLeft(ms);
      if (ms <= 0 && !fired.current) {
        fired.current = true;
        onTimeUp();
      }
    };
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [deadline, offset, stopped, onTimeUp]);
  const share = Math.min(1, left / (seconds * 1000));
  const urgent = left <= 5000;
  return (
    <div className="flex items-center gap-3" role="timer" aria-label="Tiempo restante">
      <div className="h-3 flex-1 overflow-hidden rounded-full bg-purple/15 dark:bg-cream/15">
        <div
          className={`h-full rounded-full transition-[width] duration-200 ease-linear ${urgent ? 'bg-burgundy dark:bg-burgundy-300' : 'bg-purple dark:bg-gold-300'}`}
          style={{ width: `${share * 100}%` }}
        />
      </div>
      <span
        className={`w-10 text-right font-serif text-2xl tabular-nums ${urgent ? 'text-burgundy dark:text-burgundy-300' : ''}`}
        aria-live={urgent ? 'polite' : 'off'}
      >
        {formatSeconds(left)}
      </span>
    </div>
  );
}

function QuestionCard({
  q,
  offset,
  sending,
  result,
  onChoose,
  onNext,
}: {
  q: ServedQuestion;
  offset: number;
  sending: number | null | undefined;
  result: AnswerResult | null;
  onChoose: (choice: number | null) => void;
  onNext: () => void;
}) {
  const onTimeUp = useCallback(() => onChoose(null), [onChoose]);
  const done = result != null;
  const map = q.extra?.map as MapSpec | undefined;
  return (
    <section aria-labelledby="question" className="space-y-4">
      <p className="text-base text-ink-soft dark:text-cream/75">
        Pregunta {q.position + 1} de {q.total}
      </p>
      <Countdown
        deadline={q.deadline}
        offset={offset}
        seconds={q.seconds}
        stopped={done || sending !== undefined}
        onTimeUp={onTimeUp}
      />
      <h1 id="question" className="font-serif text-2xl leading-snug">
        <Prompt text={q.prompt} />
      </h1>
      {map && (
        <Suspense
          fallback={<div className="h-64 rounded-2xl bg-purple/10 dark:bg-cream/10" aria-hidden />}
        >
          <RouteMap map={map} />
        </Suspense>
      )}
      <ul className="space-y-3">
        {q.options.map((o, i) => {
          const isRight = done && i === result.correct_choice;
          const isWrongPick = done && i === result.choice && !result.correct;
          const tone = isRight
            ? 'border-green-700 bg-green-100 text-ink dark:border-green-400 dark:bg-green-900/40 dark:text-cream'
            : isWrongPick
              ? 'border-burgundy bg-burgundy-300/30 dark:border-burgundy-300 dark:bg-burgundy/30'
              : sending === i
                ? 'border-purple bg-purple/10 dark:border-gold-300'
                : 'border-purple/25 bg-white/70 hover:border-purple dark:border-cream/20 dark:bg-night-800 dark:hover:border-gold-300';
          return (
            <li key={`${q.position}-${o}`}>
              <button
                type="button"
                disabled={done || sending !== undefined}
                onClick={() => onChoose(i)}
                className={`flex min-h-14 w-full items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left text-lg leading-snug ${tone}`}
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-purple/10 font-semibold dark:bg-cream/10">
                  {'ABCD'[i]}
                </span>
                <span className="flex-1">{o}</span>
                {isRight && <span aria-label="respuesta correcta">✔</span>}
                {isWrongPick && <span aria-label="tu respuesta">✘</span>}
              </button>
            </li>
          );
        })}
      </ul>
      {done && (
        <div className="space-y-3" aria-live="polite">
          <p
            className={`text-xl font-semibold ${result.correct ? 'text-green-800 dark:text-green-300' : 'text-burgundy dark:text-burgundy-300'}`}
          >
            {result.correct
              ? '¡Correcto!'
              : result.choice == null
                ? 'Se acabó el tiempo'
                : result.late
                  ? 'Tu respuesta llegó tarde'
                  : 'No es esa'}
          </p>
          <button type="button" className={primaryButton} onClick={onNext}>
            {result.finished ? 'Ver resultados' : 'Siguiente pregunta'}
          </button>
        </div>
      )}
    </section>
  );
}
